import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';

import { checkInHubUrl, logLevel } from 'Views/Components/_HumanWritten/CheckIn/CheckInHub/Constants';
import { CheckInRequestType } from 'Views/Components/_HumanWritten/CheckIn/CheckInHub/Types/CheckInRequestType';
import { CheckInResponseType } from 'Views/Components/_HumanWritten/CheckIn/CheckInHub/Types/CheckInResponseType';
import { ConnectionStatus } from 'Views/Components/_HumanWritten/CheckIn/CheckInHub/Types/ConnectionStatus';
import { CheckInStore } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/context/CheckInContext';
import alertToast from 'Util/ToastifyUtils';
import { appConfigStore } from 'Models/AppConfigStore';

/**
 * Ceiling on our own reconnect backoff. Without it `2 ** retryCount` walks past an hour after a
 * dozen failures, so a device that was asleep through a long outage would never come back.
 */
const MAX_RETRY_DELAY_MS = 30 * 1000;

export default class FerryCheckInSocket {
	private readonly context: CheckInStore;
	private readonly ferryTripId: string;

	private connection?: HubConnection;

	private isConnected: boolean = false;
	private isSubscribed: boolean = false;
	private isReconnecting: boolean = false;
	private retryCount: number = 0;
	private retryTimout?: NodeJS.Timeout;

	/** Set by closeConnection so a deliberate shutdown doesn't kick off a reconnect. */
	private isDisposed: boolean = false;

	private bookingIdsToIgnore: Set<string> = new Set();
	private bookingUpdateQueue: Map<string, (() => Promise<void>)[]> = new Map();
	private performingUpdateForBooking: Set<string> = new Set();

	constructor(context: CheckInStore) {
		this.context = context;
		this.ferryTripId = context.ferryTripId;

		this.openConnection().then(async () => {
			await this.subscribeToFerryTrip();
		});
	}

	public getFerryTripId() {
		return this.ferryTripId;
	}

	/**
	 * Ignore websocket event messages for a booking. Using `ignoreEventMessagesForBooking` is recommended over this
	 * method at it will automatically remove the booking from the ignore list once the action is completed.
	 * @param bookingId to ignore websocket messages for
	 */
	public addBookingToIgnore(bookingId: string) {
		this.bookingIdsToIgnore.add(bookingId);
	}

	public removeBookingToIgnore(bookingId: string) {
		this.bookingIdsToIgnore.delete(bookingId);
	}

	/**
	 * Ignore websocket event messages for a booking while performing an action.
	 * @param bookingId
	 * @param method
	 */
	public async ignoreEventMessagesForBooking(bookingId: string, method: () => Promise<void>) {
		// Add the booking to ignore while we are performing the action
		this.addBookingToIgnore(bookingId);

		await method();

		// Remove the booking from the ignore list once the actions are completed
		this.removeBookingToIgnore(bookingId);
	}

	public getConnectionStatus(): ConnectionStatus {
		if (this.isSubscribed) {
			return ConnectionStatus.Connected;
		}
		if (this.isReconnecting) {
			return ConnectionStatus.Reconnecting;
		}
		return ConnectionStatus.Disconnected;
	}

	public async getConnection() {
		if (!this.connection) {
			await this.openConnection();
		}

		return this.connection;
	}

	/**
	 * True while this socket still has, or is still working towards, a usable connection. False once
	 * it has closed for good — which is the signal to throw it away and build a new one rather than
	 * hold on to a socket that will never deliver another event.
	 */
	public isLive(): boolean {
		if (this.isDisposed) {
			return false;
		}

		// A scheduled retry counts as live, so nothing rebuilds the socket underneath a reconnect
		// that is already on its way (which would leave two connections for the one trip).
		if (this.retryTimout !== undefined) {
			return true;
		}

		const state = this.connection?.state;
		return (
			state === HubConnectionState.Connected ||
			state === HubConnectionState.Connecting ||
			state === HubConnectionState.Reconnecting
		);
	}

	public async openConnection() {
		if (this.isDisposed || this.connection) {
			return;
		}

		const connection = new HubConnectionBuilder()
			.withUrl(checkInHubUrl, {
				withCredentials: true,
				transport: HttpTransportType.WebSockets,
				skipNegotiation: true,
			})
			// Without this the connection never came back on its own: a dropped socket fired `onclose`
			// and nothing restarted it, so the device silently stopped receiving every check-in event
			// (bookings added, status changes, finalise/un-finalise) until the page was reloaded.
			.withAutomaticReconnect()
			.configureLogging(logLevel)
			.build();

		this.connection = connection;

		// Registered before start(), not after: onclose/onreconnecting/onreconnected only fire for
		// handlers attached beforehand, and a start() that fails has to be able to reach the retry.
		this.initEventHandlers();

		try {
			await connection.start();
			this.isConnected = true;
			this.retryCount = 0;
		} catch (err: any) {
			console.error(err.toString());

			// Drop the failed connection. openConnection() returns early while one is assigned, so
			// leaving it here made every subsequent retry a no-op.
			this.connection = undefined;
			this.isConnected = false;
			this.retryConnection();
		}
	}

	public async subscribeToFerryTrip() {
		if (!this.isConnected) {
			await this.openConnection();
		}

		// Sending on a connection that isn't up throws; the retry below is what gets us back.
		if (this.connection?.state !== HubConnectionState.Connected) {
			this.retryConnection();
			return;
		}

		try {
			// This socket's own trip, not the store's current one — after a reconnect the store may
			// have moved on, and re-subscribing to a trip this socket isn't for would feed the wrong
			// trip's events into the wrong page.
			await this.connection.send(CheckInRequestType[CheckInRequestType.SubscribeToCheckIn], this.ferryTripId);
		} catch (err: any) {
			console.error('Failed to subscribe to check-in updates', err);
			this.retryConnection();
		}
	}

	public async closeConnection() {
		// Marked first so the onclose handler below treats this as a deliberate shutdown rather than
		// a dropout worth reconnecting.
		this.isDisposed = true;
		this.clearRetry();

		await this.connection?.stop();

		this.connection = undefined;
		this.isConnected = false;
		this.isSubscribed = false;

		this.removeEventListeners();
	}

	private retryConnection() {
		if (this.isDisposed || this.retryTimout) {
			return;
		}

		this.retryTimout = setTimeout(async () => {
			// Cleared before the attempt, not after. This handle used to be left set forever, so the
			// guard above blocked every retry after the first one — one attempt per page load.
			this.retryTimout = undefined;
			this.retryCount++;

			await this.openConnection();
			await this.subscribeToFerryTrip();
		}, this.getRetryDelay());
	}

	private clearRetry() {
		if (this.retryTimout !== undefined) {
			clearTimeout(this.retryTimout);
			this.retryTimout = undefined;
		}
	}

	private getRetryDelay() {
		return Math.min(2 ** this.retryCount * 1000, MAX_RETRY_DELAY_MS);
	}

	private initEventHandlers() {
		this.addConnectionStatusListeners();

		this.connection?.on(CheckInResponseType[CheckInResponseType.SubscribedToCheckIn], () => {
			this.isSubscribed = true;
		});

		this.connection?.on(
			CheckInResponseType[CheckInResponseType.UpdateBookingStatus],
			this.onBookingStatusUpdate.bind(this),
		);
		this.connection?.on(CheckInResponseType[CheckInResponseType.BookingAdded], this.onBookingAdded.bind(this));
		this.connection?.on(CheckInResponseType[CheckInResponseType.BookingUpdated], this.onBookingUpdated.bind(this));
		this.connection?.on(CheckInResponseType[CheckInResponseType.BookingRemoved], this.onBookingRemoved.bind(this));
		this.connection?.on(
			CheckInResponseType[CheckInResponseType.OnlineBookingStatusChanged],
			this.onOnlineBookingStatusChanged.bind(this),
		);
	}

	private addConnectionStatusListeners() {
		this.connection?.onclose(e => {
			this.isConnected = false;
			this.isSubscribed = false;
			this.isReconnecting = false;
			this.connection = undefined;

			// Automatic reconnect has given up (or the connection closed before it engaged). Keep
			// trying on our own backoff unless this socket was closed deliberately — without it the
			// device stays deaf to every check-in event until someone reloads the page.
			if (!this.isDisposed) {
				console.error('Check-in live-update connection closed', e);
				this.retryConnection();
			}
		});

		this.connection?.onreconnecting(() => {
			this.isReconnecting = true;
			this.isConnected = false;
			this.isSubscribed = false;
		});

		this.connection?.onreconnected(() => {
			this.isConnected = true;
			this.isReconnecting = false;

			// A reconnect gets a new connection id, so the hub group joined by SubscribeToCheckIn no
			// longer contains it. Re-subscribe or the socket is connected but silent.
			this.isSubscribed = false;
			this.subscribeToFerryTrip();
		});

		document.addEventListener('visibilitychange', this.reloadCheckInPage);
	}

	private removeEventListeners() {
		document.removeEventListener('visibilitychange', this.reloadCheckInPage);
	}

	private async onBookingStatusUpdate(bookingId: string, statusId: string, stopId: string) {
		this.context.notifyCheckInActivity();
		if (this.isIgnoringBooking(bookingId)) {
			return;
		}

		this.addBookingToUpdateQueue(bookingId, async () => {
			await this.context.updateCheckInStatusToManualStatus(bookingId, statusId, stopId, {
				skipUpdateServer: true,
				triggerAlert: false,
				// Checking a booking in does more than move its status where the site sells fare
				// types: CheckInService hands the booking to AssignSeatsAtCheckInUseCase and stamps a
				// seat on everyone who had a fare type and none, and only then broadcasts this. A
				// device that merely set the status locally therefore kept showing the booking
				// unseated — the seats existed on the server and on the device that did the check-in,
				// and nowhere else until someone reloaded.
				//
				// Gated on the feature rather than refetched always: without fare types nothing else
				// about the booking changes with its status, and a refetch per status change per
				// device is a request a busy gate does not need.
				refresh: appConfigStore.passengerClassesEnabled,
			});
		});

		await this.executeNextUpdateQueued(bookingId);
	}

	private async onBookingRemoved(bookingId: string) {
		this.context.notifyCheckInActivity();
		if (this.isIgnoringBooking(bookingId)) {
			return;
		}

		this.addBookingToUpdateQueue(bookingId, async () => {
			this.context.removeBooking(bookingId);
		});

		await this.executeNextUpdateQueued(bookingId);
	}

	private async onBookingAdded(bookingId: string) {
		this.context.notifyCheckInActivity();
		if (this.isIgnoringBooking(bookingId)) {
			return;
		}

		this.addBookingToUpdateQueue(bookingId, async () => {
			await this.context.updateCheckInStatusToManualStatus(bookingId, null, undefined, {
				skipUpdateServer: true,
				triggerAlert: false,
				refresh: true,
				// See onBookingUpdated: this handler refetches the booking, it does not set its status.
				confirmAndCheckIn: false,
			});
		});

		await this.executeNextUpdateQueued(bookingId);
	}

	private async onBookingUpdated(bookingId: string) {
		this.context.notifyCheckInActivity();
		if (this.isIgnoringBooking(bookingId)) {
			return;
		}

		this.addBookingToUpdateQueue(bookingId, async () => {
			await this.context.updateCheckInStatusToManualStatus(bookingId, null, undefined, {
				// This event says "this booking changed, go and re-read it" — nothing about its check-in
				// status. `refresh: true` does that re-read; the null status is only here because the
				// method demands one.
				//
				// confirmAndCheckIn: false stops that null from being applied. Without it the handler
				// wrote checkInStatusId = undefined straight after the refetch, so a booking that was
				// altered and checked in around the same time (the alteration broadcasts BookingUpdated,
				// the check-in broadcasts UpdateBookingStatus) dropped back into Booked on every staff
				// device — while the server still had it checked in. Staff then checked it in again.
				skipUpdateServer: true,
				triggerAlert: false,
				refresh: true,
				confirmAndCheckIn: false,
			});
		});

		await this.executeNextUpdateQueued(bookingId);
	}

	private async onOnlineBookingStatusChanged() {
		this.context.notifyCheckInActivity();
		await this.context.loadFerryTrip(this.ferryTripId);
	}

	/**
	 * For now, we are maintaining the existing logic for check-ins and how they update bookings (this is to ensure that
	 * even if the WebSocket connections fail, the check-in process will continue to work). However, when a user performs
	 * an action on a booking, they also receive a notification of the change through the SignalR connection. This causes
	 * the frontend to perform a double update on the booking. To prevent this from happening, we keep track of which
	 * booking is being updated by the current user and ignore SignalR updates regarding that specific booking.
	 *
	 * @param bookingId the booking id that we want to check
	 * @private
	 */
	private isIgnoringBooking(bookingId: string) {
		return this.bookingIdsToIgnore.has(bookingId);
	}

	private addBookingToUpdateQueue(bookingId: string, method: () => Promise<void>) {
		if (this.bookingUpdateQueue.has(bookingId)) {
			this.bookingUpdateQueue.set(bookingId, [...this.bookingUpdateQueue.get(bookingId)!, method]);
		} else {
			this.bookingUpdateQueue.set(bookingId, [method]);
		}
	}

	private async executeNextUpdateQueued(bookingId: string) {
		// If we are already performing an update for this booking, do nothing
		if (this.performingUpdateForBooking.has(bookingId)) {
			return;
		}

		// Protect against another update being executed while we are waiting for the current update to complete
		this.performingUpdateForBooking.add(bookingId);

		while (this.bookingUpdateQueue.has(bookingId)) {
			const nextMethod = this.bookingUpdateQueue.get(bookingId)?.shift();

			if (!!nextMethod) {
				// Disabling the eslint rule in this instance because we want to block each method executing so that they
				// are executed sequentially.
				// eslint-disable-next-line no-await-in-loop
				await nextMethod();
			} else {
				// There are no more methods to execute for this booking
				this.bookingUpdateQueue.delete(bookingId);
			}
		}

		// We are done executing the update for this booking
		this.performingUpdateForBooking.delete(bookingId);
	}

	private reloadCheckInPage = () => {
		// if (document.visibilityState === 'visible') {
		// 	if (navigator.onLine && !this.isConnected) {
		// 		alertToast('Check-in live update connection has expired. Reconnecting...', 'info');
		//
		// 		setTimeout(async () => {
		// 			window.location.reload();
		// 		}, 1000);
		// 	}
		// }
	};
}
